import { existsSync } from "node:fs";
import { mkdir, writeFile } from "node:fs/promises";
import { homedir } from "node:os";
import { dirname, join, resolve } from "node:path";
import { installGlobalHooks } from "./global-install.js";
import { stringify as stringifyYaml } from "yaml";
import { compileComponent, compileComponentFile } from "../compiler.js";
import {
  copyCLIBinary,
  GOVERNANCE_LOCK_PATH,
  loadHookBindings,
  loadInitConfig,
  inspectGitHooks,
  materializeHooks,
  readGovernanceLock,
  SCAFFOLD_PROJECT_YAML,
  unmaterializeHooks,
} from "./governance.js";
import { runBootstrap } from "../lib/bootstrap/index.js";

/** MemoryRef shape for resume support in CLI handlers. */
export interface MemoryRef {
  id: string;
  provider: string;
  compat: string;
  created_at: string;
}

/** Build a MemoryRef from a --resume CLI option value. */
export function buildMemoryRefFromResume(resume: string): MemoryRef {
  return {
    id: resume,
    provider: "aaac",
    compat: "v1",
    created_at: new Date().toISOString(),
  };
}

/** Apply --resume from global CLI options to an invocation context. */
export function applyResumeOption<T extends Record<string, unknown>>(
  parentOpts: Record<string, unknown>,
  ctx: T,
): T & { memoryRef?: MemoryRef } {
  const resume = parentOpts.resume;
  if (typeof resume === "string" && resume.length > 0) {
    return { ...ctx, memoryRef: buildMemoryRefFromResume(resume) };
  }
  return ctx;
}

/** Log resume state when verbose mode is enabled. */
export function logResumeState(
  parentOpts: Record<string, unknown>,
  resumedState?: unknown,
): void {
  const resume = parentOpts.resume;
  if (!parentOpts.verbose || typeof resume !== "string" || resume.length === 0) {
    return;
  }
  process.stderr.write(`Resuming from memory_ref: ${resume}\n`);
  if (resumedState !== undefined) {
    process.stderr.write("Resume state loaded\n");
  }
}
import {
  ComponentCompileError,
  ComponentValidationError,
} from "../errors.js";
import type { CommandHandlers } from "../generated/program.js";
import {
  generateAll,
  writeGeneratedFiles,
  type GeneratedFile,
} from "../generators/index.js";
import type { ComponentIR } from "../ir.js";
import { ComponentParseError, parseComponentFile } from "../parser.js";
import { RefResolutionError } from "../ref-resolver.js";
import { validateComponent } from "../validation.js";

function formatError(err: unknown, file?: string): string {
  if (err instanceof ComponentParseError) {
    return err.message;
  }
  if (err instanceof ComponentValidationError) {
    return file ? `${file}: ${err.message}` : err.message;
  }
  if (err instanceof ComponentCompileError) {
    return file ? `${file}: ${err.message}` : err.message;
  }
  if (err instanceof RefResolutionError) {
    return file ? `${file}: ${err.message}` : err.message;
  }
  if (err instanceof Error) {
    return file ? `${file}: ${err.message}` : err.message;
  }
  return file ? `${file}: ${String(err)}` : String(err);
}

function fail(message: string): never {
  process.stderr.write(`${message}\n`);
  process.exit(1);
}

function resolveComponentPath(file: string | undefined): string {
  if (!file) {
    fail("Error: component file path is required");
  }
  return resolve(file);
}

function defaultOutputDir(componentPath: string): string {
  return join(dirname(componentPath), "generated");
}

function buildIntrospectionDump(ir: ComponentIR): Record<string, unknown> {
  const operations: Record<string, unknown> = {};

  for (const [name, op] of Object.entries(ir.operations)) {
    const dispatch =
      op.agentWorkflow !== undefined
        ? { type: "workflow", id: op.agentWorkflow }
        : op.agentTask !== undefined
          ? { type: "task", id: op.agentTask }
          : op.handler !== undefined
            ? { type: "handler", name: op.handler }
            : null;

    operations[name] = {
      description: op.description,
      dispatch,
      options: op.options.map((option) => ({
        name: option.name,
        required: option.required,
      })),
      failureClasses: op.errorModel.failureClasses,
      artifactSlots: op.artifactSlots,
      riskLevel: op.riskLevel,
      idempotent: op.idempotent,
      requiresConfirmation: op.requiresConfirmation,
      memoryRef: op.memoryRef,
    };
  }

  return {
    schema: ir.schema,
    info: ir.info,
    implementation: ir.implementation,
    projections: ir.projections,
    operations,
  };
}

const handleCompile: CommandHandlers["compile"] = async (file, options, parentOpts) => {
  const componentPath = resolveComponentPath(file);

  try {
    const ir = await compileComponentFile(componentPath);
    const json = JSON.stringify(ir, null, 2);

    if (options.output) {
      await writeFile(resolve(options.output), `${json}\n`, "utf-8");
      if (!parentOpts.quiet) {
        process.stdout.write(`Wrote IR to ${resolve(options.output)}\n`);
      }
      return;
    }

    process.stdout.write(`${json}\n`);
  } catch (err) {
    fail(formatError(err, componentPath));
  }
};

async function resolveEmbeddedDslData(
  ir: ComponentIR,
  componentPath: string,
): Promise<Record<string, unknown> | undefined> {
  if (!ir.embeddedDslDir || !ir.implementation) {
    return undefined;
  }

  const implPath = resolve(dirname(componentPath), ir.implementation);
  const { resolve: resolveDsl } = await import("agent-contracts");
  const dslResult = await resolveDsl(implPath);
  return dslResult.data as Record<string, unknown>;
}

function splitGeneratedFiles(files: GeneratedFile[]): {
  main: GeneratedFile[];
  embedded: GeneratedFile[];
} {
  const main: GeneratedFile[] = [];
  const embedded: GeneratedFile[] = [];

  for (const file of files) {
    if (file.targetDir) {
      embedded.push(file);
    } else {
      main.push(file);
    }
  }

  return { main, embedded };
}

const handleGenerate: CommandHandlers["generate"] = async (file, options, parentOpts) => {
  const componentPath = resolveComponentPath(file);
  const outputDir = resolve(options.outputDir ?? defaultOutputDir(componentPath));
  const dryRun = Boolean(options.dryRun);

  try {
    const ir = await compileComponentFile(componentPath);
    const resolvedDslData = await resolveEmbeddedDslData(ir, componentPath);
    const files = generateAll(ir, {
      outputDir,
      componentPath,
      resolvedDslData,
    });
    const { main, embedded } = splitGeneratedFiles(files);

    if (dryRun) {
      if (!parentOpts.quiet) {
        process.stdout.write(`Would generate ${main.length} file(s) in ${outputDir}:\n`);
        for (const generated of main) {
          const action = generated.overwrite ? "write" : "create";
          process.stdout.write(`  ${action}: ${generated.path}\n`);
          if (generated.warning) {
            process.stdout.write(`    warning: ${generated.warning}\n`);
          }
        }
        for (const generated of embedded) {
          const embeddedDir = resolve(dirname(componentPath), generated.targetDir!);
          const action = generated.overwrite ? "write" : "create";
          process.stdout.write(
            `  ${action}: ${join(embeddedDir, generated.path)} (embedded DSL)\n`,
          );
        }
      }
      return;
    }

    const result = await writeGeneratedFiles(main, outputDir);

    for (const generated of embedded) {
      const embeddedDir = resolve(dirname(componentPath), generated.targetDir!);
      await mkdir(embeddedDir, { recursive: true });
      await writeFile(join(embeddedDir, generated.path), generated.content, "utf-8");
      result.written.push(join(generated.targetDir!, generated.path));
    }

    if (!parentOpts.quiet) {
      if (result.written.length > 0) {
        process.stdout.write(`Written:\n`);
        for (const path of result.written) {
          process.stdout.write(`  ${path}\n`);
        }
      }
      if (result.skipped.length > 0) {
        process.stdout.write(`Skipped:\n`);
        for (const path of result.skipped) {
          process.stdout.write(`  ${path}\n`);
        }
      }
      for (const warning of result.warnings) {
        process.stderr.write(`Warning: ${warning}\n`);
      }
    }
  } catch (err) {
    fail(formatError(err, componentPath));
  }
};

const handleValidate: CommandHandlers["validate"] = async (file, options, parentOpts) => {
  const componentPath = resolveComponentPath(file);
  const basePath = dirname(componentPath);

  try {
    const dsl = await parseComponentFile(componentPath);
    const ir = compileComponent(dsl, { basePath });
    await validateComponent(ir, { basePath });

    if (!parentOpts.quiet) {
      process.stdout.write(`Validation passed: ${componentPath}\n`);
    }
  } catch (err) {
    fail(formatError(err, componentPath));
  }
};

const handleIntrospect: CommandHandlers["introspect"] = async (file, options, _parentOpts) => {
  const componentPath = resolveComponentPath(file);
  const format = options.format ?? "yaml";

  if (format !== "json" && format !== "yaml") {
    fail(`Error: invalid format "${format}". Use json or yaml.`);
  }

  try {
    const ir = await compileComponentFile(componentPath);
    const dump = buildIntrospectionDump(ir);

    if (format === "json") {
      process.stdout.write(`${JSON.stringify(dump, null, 2)}\n`);
      return;
    }

    process.stdout.write(stringifyYaml(dump));
  } catch (err) {
    fail(formatError(err, componentPath));
  }
};

const DEFAULT_PROJECT_YAML = "project.yaml";

const handleInit: CommandHandlers["init"] = async (projectRoot, options, parentOpts) => {
  const root = resolve(projectRoot ?? ".");
  const quiet = Boolean(parentOpts.quiet);
  const projectYamlRel = options.projectYaml ?? DEFAULT_PROJECT_YAML;
  const projectYamlPath = join(root, projectYamlRel);
  const bootstrap = Boolean(options.bootstrap);
  const artifactsOnly = Boolean(options.artifactsOnly);
  const noLlm = Boolean(options.noLlm);

  // 1. Copy CLI binary (SHA256 idempotent).
  //    options.binaryPath is for programmatic/test use; production falls back to process.argv[1].
  const srcBinary = resolve(options.binaryPath ?? process.argv[1]);
  const binaryResult = await copyCLIBinary(srcBinary, root);
  if (!quiet && binaryResult.action !== "unchanged") {
    process.stdout.write(`  ${binaryResult.action}: ${binaryResult.path}\n`);
  }

  // 2. Scaffold project.yaml when absent (skip when full bootstrap will write manifest).
  const skipScaffold = bootstrap && !artifactsOnly;
  if (!existsSync(projectYamlPath) && !skipScaffold) {
    await writeFile(projectYamlPath, SCAFFOLD_PROJECT_YAML, "utf8");
    if (!quiet) {
      process.stdout.write(`  created: ${DEFAULT_PROJECT_YAML}\n`);
    }
  }

  // 2b. Bootstrap governance manifest (--bootstrap).
  if (bootstrap) {
    try {
      await runBootstrap({
        projectRoot: root,
        projectYamlPath,
        noLlm,
        artifactsOnly,
        quiet,
      });
    } catch (err) {
      fail(`Error: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // 3. Check for .git/ — skip hook materialization with warning if absent.
  const gitDir = join(root, ".git");
  if (!existsSync(gitDir)) {
    process.stderr.write(
      `Warning: .git/ not found in ${root}; skipping hook materialization\n`,
    );
    if (!quiet) {
      process.stdout.write(`Initialized project at ${root}\n`);
    }
    return;
  }

  // 4. Parse project.yaml and materialize configured hooks.
  let config;
  try {
    config = await loadInitConfig(projectYamlPath);
  } catch (err) {
    process.stderr.write(
      `Warning: Could not parse ${DEFAULT_PROJECT_YAML}: ${err instanceof Error ? err.message : String(err)}\n`,
    );
    if (!quiet) {
      process.stdout.write(`Initialized project at ${root}\n`);
    }
    return;
  }

  if (!config.environments) {
    if (!quiet) {
      process.stdout.write(
        `  no environments configured in ${DEFAULT_PROJECT_YAML}; skipping hooks\n`,
      );
      process.stdout.write(`Initialized project at ${root}\n`);
    }
    return;
  }

  const hookCommands = await loadHookBindings(root, config.bindings?.hooks);

  const result = await materializeHooks({
    projectRoot: root,
    environments: config.environments,
    observabilityBinding: config.bindings?.observability,
    artifactContracts: config.artifacts,
    hookCommands,
  });

  if (!quiet) {
    for (const m of result.materialized) {
      if (m.action !== "unchanged") {
        process.stdout.write(`  ${m.action}: ${m.path}\n`);
      }
    }
    process.stdout.write(`Initialized project at ${root}\n`);
  }
};

const handleUpdate: CommandHandlers["update"] = async (projectRoot, options, parentOpts) => {
  const root = resolve(projectRoot ?? ".");
  const quiet = Boolean(parentOpts.quiet);
  const projectYamlPath = join(root, options.projectYaml ?? DEFAULT_PROJECT_YAML);

  if (!existsSync(projectYamlPath)) {
    fail(
      `Error: ${DEFAULT_PROJECT_YAML} not found at ${root}. Run 'aaac init' first.`,
    );
  }

  // Skip hook materialization with warning if .git/ is absent.
  const gitDir = join(root, ".git");
  if (!existsSync(gitDir)) {
    process.stderr.write(
      `Warning: .git/ not found in ${root}; skipping hook materialization\n`,
    );
    return;
  }

  let config;
  try {
    config = await loadInitConfig(projectYamlPath);
  } catch (err) {
    fail(
      `Error: Could not parse ${DEFAULT_PROJECT_YAML}: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (!config.environments) {
    if (!quiet) {
      process.stdout.write(`No environments configured in ${DEFAULT_PROJECT_YAML}\n`);
    }
    return;
  }

  const hookCommands = await loadHookBindings(root, config.bindings?.hooks);

  const gitHookNames = config.environments.git?.hooks ?? [];
  if (gitHookNames.length > 0) {
    const issues = await inspectGitHooks(root, gitHookNames);
    for (const issue of issues) {
      const relPath = join(".git/hooks", issue.hookName);
      if (issue.kind === "missing") {
        process.stderr.write(
          `Warning: git hook '${relPath}' is missing; will repair\n`,
        );
      } else if (issue.kind === "empty") {
        process.stderr.write(
          `Warning: git hook '${relPath}' is empty; will repair\n`,
        );
      } else {
        process.stderr.write(
          `Warning: git hook '${relPath}' is missing aaac managed block; will repair\n`,
        );
      }
    }
  }

  const result = await materializeHooks({
    projectRoot: root,
    environments: config.environments,
    observabilityBinding: config.bindings?.observability,
    artifactContracts: config.artifacts,
    hookCommands,
  });

  if (!quiet) {
    for (const m of result.materialized) {
      process.stdout.write(`  ${m.action}: ${m.path}\n`);
    }
    process.stdout.write(`Updated hooks at ${root}\n`);
  }
};

const handleUninstall: CommandHandlers["uninstall"] = async (
  projectRoot,
  _options,
  parentOpts,
) => {
  const root = resolve(projectRoot ?? ".");
  const quiet = Boolean(parentOpts.quiet);

  const lock = await readGovernanceLock(root);
  if (!lock || lock.entries.length === 0) {
    if (!quiet) {
      process.stdout.write(
        `Nothing to uninstall (no governance lock found at ${join(root, GOVERNANCE_LOCK_PATH)})\n`,
      );
    }
    return;
  }

  await unmaterializeHooks(root, lock.entries);

  if (!quiet) {
    process.stdout.write(`Uninstalled governance hooks from ${root}\n`);
  }
};

const handleInstall: CommandHandlers["install"] = async (options, parentOpts) => {
  const quiet = Boolean(parentOpts.quiet);
  const dryRun = Boolean(options.dryRun);
  const home = homedir();
  if (!home) {
    fail("Error: could not resolve home directory ($HOME)");
  }

  // --global is currently the only supported scope; it is implied even when the
  // flag is omitted so the one-command UX works out of the box.
  let actions;
  try {
    actions = await installGlobalHooks({
      home,
      dryRun,
      cursor: !options.skipCursor,
      claude: !options.skipClaude,
      git: !options.skipGit,
    });
  } catch (err) {
    fail(`Error: ${err instanceof Error ? err.message : String(err)}`);
  }

  if (!quiet) {
    const prefix = dryRun ? "[dry-run] " : "";
    for (const a of actions) {
      const note = a.note ? ` — ${a.note}` : "";
      process.stdout.write(`  ${prefix}${a.action}: [${a.target}] ${a.path}${note}\n`);
    }
    process.stdout.write(
      `${dryRun ? "Previewed" : "Installed"} user-global observability hooks (Cursor + Claude + Git)\n`,
    );
  }
};

export const handlers: CommandHandlers = {
  compile: handleCompile,
  generate: handleGenerate,
  validate: handleValidate,
  introspect: handleIntrospect,
  init: handleInit,
  update: handleUpdate,
  uninstall: handleUninstall,
  install: handleInstall,
};
